import { Clock } from "lucide-react";

import { formatDate, formatDateTime, formatVND } from "@/lib/format";
import { Badge } from "@/ui/badge";
import type { MyAffiliateLedger as Ledger } from "@/features/affiliates/my-code-api";
import { periodLabel } from "@/features/affiliates/milestone-progress";
import {
  LedgerSectionTitle,
  MilestoneProgressSection,
} from "@/features/affiliates/components/MyAffiliateMilestoneProgress";
import { HintNote } from "@/components/HintTip";

type Order = Ledger["recentOrders"][number];
type Milestone = Ledger["milestones"][number];
type MilestoneRule = Ledger["config"]["orderMilestones"][number];

// Tên thương hiệu theo mã AFF ghi trên đơn (CHỮ HOA). Mã lạ thì hiện nguyên mã
// — không đoán tên.
const AFF_BRAND_NAMES: Record<string, string> = {
  GNAT: "Gạo Nâu Ảnh Thẻ",
  GNPF: "Gạo Nâu Profile",
  GNCA: "Gạo Nâu Chụp Ảnh",
  GNTR: "Gạo Nâu Travel",
};

// Màu nhãn theo trạng thái đơn; chữ trạng thái do AFF gửi (statusLabel) nên
// màu không phải thứ duy nhất mang nghĩa.
const ORDER_BADGE: Record<string, "green" | "yellow" | "secondary" | "red"> = {
  eligible: "green",
  pending: "yellow",
  paid: "secondary",
  cancelled: "red",
  self_referral_blocked: "red",
};

const RECENT_ORDER_LIMIT = 5;

/**
 * Sổ hoa hồng giới thiệu của nhân viên — CHỈ ĐỂ XEM. Mọi con số là bản sao sổ
 * AFF gửi về; tiền hoa hồng vẫn chi theo kỳ chi AFF, ở đây không có nút nào
 * đụng tới tiền (QĐ 0268; mẫu màn CEO duyệt 1a · 2a ngày 01/10/2026).
 */
export function MyAffiliateLedger({ ledger }: { ledger: Ledger }) {
  const { stats, config } = ledger;
  const orders = ledger.recentOrders.slice(0, RECENT_ORDER_LIMIT);
  const hasRules = config.orderMilestones.length > 0 || config.revenueMilestones.length > 0;

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <StatTile
          highlight
          label="Chờ kỳ chi"
          value={formatVND(stats.eligibleAmount)}
          hints={[
            config.payoutScheduleLabel ? `Chi ${config.payoutScheduleLabel}` : "",
            stats.pendingMilestoneAmount > 0
              ? `+ ${formatVND(stats.pendingMilestoneAmount)} thưởng mốc chờ trả`
              : "",
          ]}
        />
        <StatTile
          label="Đã nhận"
          value={formatVND(stats.paidAmount)}
          hints={[
            stats.paidMilestoneAmount > 0
              ? `+ ${formatVND(stats.paidMilestoneAmount)} thưởng mốc`
              : "",
          ]}
        />
        <StatTile label="Đơn đã ghi nhận" value={String(stats.recordedOrders)} />
        <StatTile label="Đơn đang chờ chốt" value={String(stats.pendingOrders)} />
      </div>

      <div className="rounded-md border border-border bg-surface-2 px-3 py-2.5 text-sm">
        <div>
          <span className="font-semibold">Hoa hồng {config.commissionRate}%</span> mỗi đơn
          {config.customerDiscountRate > 0
            ? ` · khách dùng mã được giảm ${config.customerDiscountRate}%`
            : ""}
        </div>
        <div className="text-muted-foreground">
          Chi khi đủ từ {formatVND(config.minimumPayout)}
          {config.attributionDays > 0
            ? ` · khách đặt trong ${config.attributionDays} ngày sau khi bấm link vẫn tính cho bạn`
            : ""}
        </div>
      </div>

      {ledger.milestoneProgress && hasRules ? (
        <MilestoneProgressSection progress={ledger.milestoneProgress} config={config} />
      ) : null}

      <section aria-label="Đơn gần đây">
        <LedgerSectionTitle
          title="Đơn gần đây"
          aside={orders.length > 0 ? `${orders.length} đơn mới nhất` : ""}
        />
        {orders.length > 0 ? (
          <ul className="mt-1.5 divide-y divide-border overflow-hidden rounded-md border border-border">
            {orders.map((order, index) => (
              <OrderRow key={`${order.createdAt}-${index}`} order={order} />
            ))}
          </ul>
        ) : (
          <div className="mt-1.5 rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            <div className="font-semibold text-foreground">Chưa có đơn nào dùng mã của bạn</div>
            Gửi mã cho khách khi tư vấn — đơn khách đặt bằng mã sẽ hiện ở đây.
          </div>
        )}
      </section>

      {hasRules ? (
        <section aria-label="Mốc thưởng quý">
          <LedgerSectionTitle title="Mốc thưởng quý" aside="Mỗi quý nhận một mốc cao nhất" />
          <ul className="mt-1.5 divide-y divide-border overflow-hidden rounded-md border border-border">
            {config.orderMilestones.map((rule) => (
              <RuleRow key={`orders-${rule.id}`} rule={rule} title={`Đạt ${rule.threshold} đơn`} />
            ))}
            {config.revenueMilestones.map((rule) => (
              <RuleRow
                key={`revenue-${rule.id}`}
                rule={rule}
                title={`Đạt doanh số ${formatVND(rule.threshold)}`}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {ledger.milestones.length > 0 ? (
        <section aria-label="Thưởng mốc đã đạt">
          <LedgerSectionTitle title="Thưởng mốc đã đạt" aside="" />
          <ul className="mt-1.5 divide-y divide-border overflow-hidden rounded-md border border-border">
            {ledger.milestones.map((milestone, index) => (
              <AwardRow key={`${milestone.period}-${milestone.table}-${index}`} milestone={milestone} />
            ))}
          </ul>
        </section>
      ) : null}

      <HintNote>
        <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          {ledger.snapshotAt ? `Số liệu cập nhật lúc ${formatDateTime(ledger.snapshotAt)}. ` : ""}
          Sổ chỉ để xem — tiền hoa hồng chi theo kỳ chi như hiện nay.
        </span>
      </HintNote>
    </>
  );
}

function StatTile({
  label,
  value,
  hints = [],
  highlight = false,
}: {
  label: string;
  value: string;
  hints?: string[];
  highlight?: boolean;
}) {
  return (
    <div
      className={
        highlight
          ? "rounded-md border border-brand/25 bg-[var(--color-brand-glow)] px-3 py-2.5"
          : "rounded-md border border-border bg-surface px-3 py-2.5"
      }
    >
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={`text-xl font-semibold leading-tight tabular-nums ${highlight ? "text-brand-dark" : ""}`}
      >
        {value}
      </div>
      {hints
        .filter((hint) => hint !== "")
        .map((hint) => (
          <div key={hint} className="mt-0.5 text-xs text-muted-foreground">
            {hint}
          </div>
        ))}
    </div>
  );
}

function OrderRow({ order }: { order: Order }) {
  const brand = AFF_BRAND_NAMES[order.brand] ?? order.brand;
  // Đơn chưa chốt thì máy chủ KHÔNG gửi ô tiền — vắng khác 0 ₫, không in "0 ₫".
  const settled = order.commissionAmount != null;
  const basis =
    order.rate != null && order.baseAmount != null
      ? ` · ${order.rate}% của ${formatVND(order.baseAmount)}`
      : "";
  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5 px-3 py-2.5">
      <div className="font-medium">
        {brand}
        {order.productLineLabel ? ` · ${order.productLineLabel}` : ""}
      </div>
      <div
        className={
          settled
            ? "text-right font-semibold tabular-nums"
            : "text-right text-[var(--color-text-dim)]"
        }
      >
        {settled ? `+ ${formatVND(order.commissionAmount)}` : "chưa chốt"}
      </div>
      <div className="text-xs text-muted-foreground">
        {formatDate(order.createdAt)}
        {basis}
      </div>
      <div className="text-right">
        <Badge variant={ORDER_BADGE[order.status] ?? "secondary"}>{order.statusLabel}</Badge>
      </div>
    </li>
  );
}

function RuleRow({ rule, title }: { rule: MilestoneRule; title: string }) {
  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5 px-3 py-2.5">
      <div className="font-medium">{title}</div>
      <div className="text-right font-semibold tabular-nums">{formatVND(rule.reward)}</div>
      {rule.minRevenue != null && rule.minRevenue > 0 ? (
        <div className="col-span-2 text-xs text-muted-foreground">
          và doanh số từ {formatVND(rule.minRevenue)}
        </div>
      ) : null}
    </li>
  );
}

function AwardRow({ milestone }: { milestone: Milestone }) {
  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5 px-3 py-2.5">
      <div className="font-medium">{periodLabel(milestone.period)}</div>
      <div className="text-right font-semibold tabular-nums">{formatVND(milestone.amount)}</div>
      <div className="text-xs text-muted-foreground">
        {milestone.table === "revenue" ? "Mốc doanh số" : "Mốc số đơn"}
      </div>
      <div className="text-right">
        <Badge variant={milestone.status === "paid" ? "secondary" : "yellow"}>
          {milestone.statusLabel}
        </Badge>
      </div>
    </li>
  );
}
